import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { sendVerificationEmail } from '@/lib/auth';
import { Mail, Lock, User, Eye, EyeOff, Loader2, CheckCircle2, Calendar } from 'lucide-react';

type Step = 'form' | 'verify';

const TRUST = [
  { stat: "5,000+", label: "SAT practice questions" },
  { stat: "2,500+", label: "ACT practice questions" },
  { stat: "97%",    label: "of students improve" },
];

export function SignUpPage() {
  const { register } = useAuth();
  const [step, setStep]                   = useState<Step>('form');
  const [name, setName]                   = useState('');
  const [email, setEmail]                 = useState('');
  const [password, setPassword]           = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [birthYear, setBirthYear]           = useState('');
  const [showPassword, setShowPassword]   = useState(false);
  const [loading, setLoading]             = useState(false);
  const [error, setError]                 = useState<string | null>(null);

  const currentYear = new Date().getFullYear();
  const birthYears = Array.from({ length: 100 }, (_, i) => currentYear - i);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!birthYear) { setError('Please enter your birth year to continue'); return; }
    const age = new Date().getFullYear() - parseInt(birthYear);
    if (age < 13) {
      setError('You must be 13 years of age or older to create an account (COPPA requirement).');
      return;
    }
    if (password !== confirmPassword) { setError('Passwords do not match'); return; }
    if (password.length < 8) { setError('Password must be at least 8 characters'); return; }
    setLoading(true);
    try {
      await register(email, password, name);
      await sendVerificationEmail(`${window.location.origin}/verify`);
      setStep('verify');
    } catch (err: any) {
      setError(err.message || 'Failed to create account. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  /* ── Verify step ── */
  if (step === 'verify') {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f9fc] px-4">
        <div className="bg-white border border-[#dde3ed] rounded-2xl shadow-sm p-10 w-full max-w-md text-center">
          <div className="w-16 h-16 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
            <CheckCircle2 className="w-8 h-8 text-green-600" />
          </div>
          <h1 className="text-2xl font-extrabold text-[#1a2435] mb-2">Check your email</h1>
          <p className="text-[#5a6a7e] text-sm mb-6">
            We've sent a verification link to <strong className="text-[#1a2435]">{email}</strong>.
            Click the link to verify your account.
          </p>
          <button
            onClick={() => (window.location.href = '/login')}
            className="w-full border border-[#dde3ed] rounded-xl py-2.5 text-sm font-semibold text-[#1a2435] hover:bg-[#f8f9fc] transition-colors mb-3"
          >
            Go to login
          </button>
          <p className="text-xs text-[#5a6a7e]">
            Didn't receive it?{' '}
            <button
              onClick={() => sendVerificationEmail(`${window.location.origin}/verify`)}
              className="text-[#1a3a6b] font-semibold hover:underline"
            >
              Resend
            </button>
          </p>
        </div>
      </div>
    );
  }

  /* ── Sign-up form ── */
  return (
    <div className="min-h-screen flex">

      {/* ── Left panel ── */}
      <div className="hidden md:flex md:w-1/2 bg-gradient-to-br from-[#070f24] via-[#0d2248] to-[#1a3a6b] flex-col justify-between p-12 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-80 h-80 rounded-full bg-[#f5a623]/10 blur-3xl pointer-events-none" />

        <a href="/">
          <img
            src="/logo.svg"
            alt="Scholar Sphere"
            className="h-11 w-auto brightness-0 invert"
          />
        </a>

        <div>
          <h1 className="text-4xl font-black text-white leading-tight mb-4">
            Start for free.<br />
            <span className="text-[#f5a623]">Level up your score.</span>
          </h1>
          <p className="text-white/60 text-base leading-relaxed mb-10">
            Join thousands of students practicing SAT, ACT, and AP questions every day.
          </p>
          <ul className="space-y-4">
            {[
              "Access the full SAT & ACT question bank instantly",
              "Review every session — see every mistake, every explanation",
              "Personalized AI assistant on every question",
              "Add expert 1:1 tutoring whenever you're ready",
            ].map((item) => (
              <li key={item} className="flex items-center gap-3 text-sm text-white/80">
                <span className="w-5 h-5 rounded-full bg-[#f5a623]/20 border border-[#f5a623]/40 flex items-center justify-center flex-shrink-0 text-[#f5a623] text-xs font-bold">✓</span>
                {item}
              </li>
            ))}
          </ul>
        </div>

        <div className="flex gap-8 pt-8 border-t border-white/10">
          {TRUST.map(({ stat, label }) => (
            <div key={label}>
              <div className="text-2xl font-black text-[#f5a623]">{stat}</div>
              <div className="text-xs text-white/50 mt-0.5">{label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Right panel ── */}
      <div className="flex-1 bg-white flex flex-col items-center justify-center px-6 py-12 overflow-y-auto">
        <a href="/" className="mb-8 md:hidden">
          <img src="/logo.svg" alt="Scholar Sphere" className="h-10" />
        </a>

        <div className="w-full max-w-sm">
          <h2 className="text-2xl font-extrabold text-[#1a2435] mb-1">Create your account</h2>
          <p className="text-[#5a6a7e] text-sm mb-7">It's free. No credit card required.</p>

          {error && (
            <div className="mb-4 p-3 rounded-xl bg-red-50 border border-red-200 text-red-600 text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Name */}
            <div>
              <label className="block text-xs font-semibold text-[#1a2435] mb-1.5">Full name</label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e]" />
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Jane Smith"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-[#dde3ed] rounded-xl text-sm text-[#1a2435] placeholder:text-[#5a6a7e]/60 focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] transition-colors bg-[#f8f9fc]"
                />
              </div>
            </div>

            {/* Birth Year */}
            <div>
              <label className="block text-xs font-semibold text-[#1a2435] mb-1.5">Birth year <span className="text-[#5a6a7e] font-normal">(you must be 13 or older)</span></label>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e] pointer-events-none" />
                <select
                  value={birthYear}
                  onChange={(e) => setBirthYear(e.target.value)}
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-[#dde3ed] rounded-xl text-sm text-[#1a2435] focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] transition-colors bg-[#f8f9fc] appearance-none"
                >
                  <option value="">Select your birth year</option>
                  {birthYears.map((year) => (
                    <option key={year} value={year}>{year}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-[#1a2435] mb-1.5">Email</label>
              <div className="relative">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e]" />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  required
                  autoComplete="email"
                  className="w-full pl-10 pr-4 py-2.5 border border-[#dde3ed] rounded-xl text-sm text-[#1a2435] placeholder:text-[#5a6a7e]/60 focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] transition-colors bg-[#f8f9fc]"
                />
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-semibold text-[#1a2435] mb-1.5">Password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  minLength={8}
                  className="w-full pl-10 pr-10 py-2.5 border border-[#dde3ed] rounded-xl text-sm text-[#1a2435] placeholder:text-[#5a6a7e]/60 focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] transition-colors bg-[#f8f9fc]"
                />
                <button type="button" onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#5a6a7e] hover:text-[#1a2435] transition-colors">
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              <p className="text-xs text-[#5a6a7e] mt-1">At least 8 characters</p>
            </div>

            {/* Confirm */}
            <div>
              <label className="block text-xs font-semibold text-[#1a2435] mb-1.5">Confirm password</label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#5a6a7e]" />
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full pl-10 pr-4 py-2.5 border border-[#dde3ed] rounded-xl text-sm text-[#1a2435] placeholder:text-[#5a6a7e]/60 focus:outline-none focus:ring-2 focus:ring-[#1a3a6b]/20 focus:border-[#1a3a6b] transition-colors bg-[#f8f9fc]"
                />
              </div>
            </div>

            <button
              type="submit"
              disabled={loading}
              className="w-full flex items-center justify-center gap-2 bg-[#f5a623] hover:bg-[#e09310] text-[#12265a] font-bold py-2.5 rounded-xl transition-colors text-sm disabled:opacity-60"
            >
              {loading ? <><Loader2 className="w-4 h-4 animate-spin" /> Creating account…</> : 'Create free account'}
            </button>

            <p className="text-xs text-center text-[#5a6a7e]">
              By signing up you confirm you are 13 or older and agree to our{' '}
              <a href="/terms" className="text-[#1a3a6b] hover:underline">Terms</a>{' '}and{' '}
              <a href="/privacy" className="text-[#1a3a6b] hover:underline">Privacy Policy</a>
            </p>
          </form>

          <p className="text-center text-sm text-[#5a6a7e] mt-6">
            Already have an account?{' '}
            <a href="/login" className="text-[#1a3a6b] font-semibold hover:underline">Sign in</a>
          </p>
        </div>
      </div>
    </div>
  );
}
